/**
 * partnerAgreements.ts — where each of a partner's vehicles stands on its Vehicle Schedule.
 *
 * THIS IS THE BACKFILL BOARD'S ARITHMETIC, AND IT IS DELIBERATELY THE SERVER'S. [D-B48] made a
 * signed Vehicle Schedule a precondition of the driver's hire purchase signature on any vehicle a
 * third-party partner owns, and every live third-party contract woke up on promote day missing one.
 * The admin needs one page that answers "whose schedule is still outstanding" without opening each
 * contract, so this module merges two lists the app already reads — the partner's contracted
 * vehicles and the referent rows on `contract_partner_agreements` — into one row per contract.
 *
 * THE STATE MACHINE IS agreement_sign's OWN DISJUNCTION, NOT A SECOND OPINION. The gate at
 * `20260908020000:1672-1690` is satisfied by a referent for (this contract, this partner, this
 * vehicle) whose frozen rate still matches the contract's, AND which either carries `signed_at`
 * or has a SIGNED `PARTNER` seat on a live document. Both limbs are read here, because they are
 * hours apart in practice: the partner signs in fleet-partner, and `signed_at` lands only when an
 * admin countersigns and `agreement_finalize` seals the PDF. A board that called that window
 * "out for signature" would send an admin to chase a partner who has already signed.
 *
 * THERE IS NO PAPER STATE. [D-B49] revoked `signed_at`, `agreement_url` and `origin` from both
 * client roles on all five legacy arcs, so no admin can file a scan against this arc and none ever
 * has — `origin` names no row on `contract_partner_agreements`. The three states below are the
 * whole vocabulary.
 *
 * REACT-FREE ON PURPOSE: `vitest.config.ts:11-12` collects `src/**\/*.test.ts` under the node
 * environment, so the arithmetic this page reports is pinned by tests and the component that
 * draws it holds no rules of its own.
 */

import { isLiveDocument } from '@/lib/agreements/constants';

/** How far a contract has got with its schedule. */
export type PartnerScheduleState =
    /** The partner has executed it — sealed, or signed and awaiting the countersignature. */
    | 'signed'
    /** A live document is out and the partner has not signed it yet. */
    | 'out'
    /** Nothing has been raised. */
    | 'missing';

/**
 * One row of `get_partner_vehicle_pipeline(p_group => 'contracts')`, narrowed to what this page
 * reads. `contract_id` IS NULLABLE HERE AND THE RPC IS WHY: the contracts group admits a vehicle
 * whose `contract_stage` is `sold` on the strength of a `vehicle_sales` row alone, which a vehicle
 * that never carried a contract can have (`20260725180000` `get_partner_vehicle_pipeline`, the
 * `WHEN vs.id IS NOT NULL THEN 'sold'` arm above `latest_c.id AS contract_id`).
 */
export interface PartnerPipelineVehicle {
    vehicle_id: number;
    contract_id: number | null;
    make: string | null;
    model: string | null;
    reg_number: string | null;
    contract_stage: string | null;
    /** `COALESCE(contracts.management_rate, 0)`, so 0 means "nobody has stated one". */
    management_rate: number | null;
}

/** One `contract_partner_agreements` row, read directly under the admin's SELECT policy. */
export interface PartnerScheduleReferent {
    id: number;
    contract_id: number;
    signed_at: string | null;
    /**
     * THE CASH PRICE THE SCHEDULE STATES [D-B59] — the contract's `cash_price`, rounded to the
     * pesewa and copied onto the record by `agreement_create` at generation. The column keeps its
     * historical name. This roster is the one surface that reads the copy rather than the contract,
     * because what a partner signed is the figure on their record.
     */
    all_in_cost: number | null;
    /**
     * Derived from the contract and frozen at the seal, so a vehicle moved onto a different
     * contract afterwards leaves this pointing at the vehicle the partner actually signed for.
     * The gate compares it to the contract's live `vehicle_id` and refuses on a mismatch.
     *
     * NOT NULLABLE, and the column says so (`20260908010000` `vehicle_id bigint NOT NULL`) — which
     * is why the gate spells this limb without the `IS NULL` escape it gives the rate one line
     * below it. A trigger derives the value on every write, so there is no unstamped row to admit.
     */
    vehicle_id: number;
    /** Frozen at the partner's signature; NULL on a row raised against a rateless contract. */
    management_rate: number | null;
}

/** One `agreement_documents` row on the fifth arc, with its seats. */
export interface PartnerScheduleDocument {
    contract_partner_agreement_id: number | null;
    status: string | null;
    signatures: Array<{ party: string | null; status: string | null }>;
}

export interface PartnerAgreementsInput {
    vehicles: PartnerPipelineVehicle[];
    referents: PartnerScheduleReferent[];
    documents: PartnerScheduleDocument[];
}

export interface PartnerAgreementRow {
    contractId: number;
    vehicleId: number;
    /** "Toyota Vitz", or the registration alone when the model is missing. */
    vehicle: string;
    registration: string | null;
    stage: string | null;
    /** The contract's live rate, or null where it is absent or zero. */
    managementRate: number | null;
    state: PartnerScheduleState;
    signedAt: string | null;
    /** The referent's `all_in_cost`: the Cash price the schedule states, shown under that header. */
    allInCost: number | null;
    /** Signed by the partner, not yet countersigned and sealed. */
    awaitingSeal: boolean;
    /**
     * Executed at a fee the contract no longer states, so the gate refuses the driver's signature
     * until the schedule is voided and reissued (`20260908020000:1679-1682`).
     */
    rateSuperseded: boolean;
    /**
     * Executed against a vehicle this contract no longer names — the reassignment [D-B48] chose to
     * DETECT rather than prevent. The gate's third limb (`20260908020000:1677-1678`) compares the
     * frozen `vehicle_id` to the contract's live one, so a schedule in this state opens no door
     * however signed it looks; the remedy is the same void-and-reissue.
     */
    vehicleSuperseded: boolean;
    /** Nothing can be generated yet: `agreement_create` refuses a NULL or zero management rate. */
    blockedByRate: boolean;
    /** Whether this contract is still one the schedule can do anything for. */
    open: boolean;
}

export interface PartnerAgreementsSummary {
    /** Open contracts — the denominator the sentence and the gate both mean. */
    total: number;
    signed: number;
    outstanding: number;
    /** Contracts already completed, terminated or sold, which the sentence leaves out. */
    closed: number;
}

/**
 * The stages a Vehicle Schedule still has work to do on.
 *
 * A DRAFT is in the set because the gate is what stops it activating, and an active contract is
 * in it because that is the population [D-B48]'s promote-day effect names. Completed, terminated
 * and sold contracts are history: raising a schedule against one changes nothing anybody signs,
 * so they are shown but never counted, and the tracker can reach its own total.
 */
const OPEN_STAGES = new Set(['draft', 'active']);

/** Two decimal places, the same currency test `agreement_sign` applies to the frozen rate. */
function round2(value: number): number {
    return Math.round(value * 100) / 100;
}

function vehicleLabel(row: PartnerPipelineVehicle): string {
    const name = [row.make, row.model].map((part) => (part ?? '').trim()).filter(Boolean).join(' ');
    return name || row.reg_number?.trim() || `Vehicle #${row.vehicle_id}`;
}

/** Has the partner's own seat on this document been signed? */
function partnerSeatSigned(document: PartnerScheduleDocument): boolean {
    return document.signatures.some(
        (seat) => seat.party === 'PARTNER' && seat.status === 'SIGNED',
    );
}

/**
 * One row per contracted vehicle, newest contract first.
 *
 * VEHICLES WITH NO CONTRACT ARE DROPPED RATHER THAN REPORTED AS MISSING. The schedule is per
 * contract — every figure on it derives from one — so a vehicle that carries none has nothing
 * outstanding, and counting it would put a permanent shortfall in the sentence above the table.
 */
export function partnerAgreementRows(input: PartnerAgreementsInput): PartnerAgreementRow[] {
    const referentByContract = new Map<number, PartnerScheduleReferent>();
    for (const referent of input.referents) referentByContract.set(referent.contract_id, referent);

    // Live documents only, keyed by the arc they sit on. A voided document leaves its referent
    // standing ([D-B40] deletes the document, not the legacy row), so an arc with nothing live on
    // it is back to `missing` — which is the state the board must offer Generate from.
    const liveByArc = new Map<number, PartnerScheduleDocument[]>();
    for (const document of input.documents) {
        const arc = document.contract_partner_agreement_id;
        if (arc == null || !isLiveDocument(document.status)) continue;
        const held = liveByArc.get(arc);
        if (held) held.push(document);
        else liveByArc.set(arc, [document]);
    }

    const rows: PartnerAgreementRow[] = [];
    for (const vehicle of input.vehicles) {
        const contractId = vehicle.contract_id;
        if (contractId == null) continue;

        const referent = referentByContract.get(contractId) ?? null;
        const live = referent ? liveByArc.get(referent.id) ?? [] : [];
        const seatSigned = live.some(partnerSeatSigned);
        const sealed = !!referent?.signed_at;
        const signed = sealed || seatSigned;

        const liveRate = vehicle.management_rate ?? 0;
        const frozenRate = referent?.management_rate ?? null;

        rows.push({
            contractId,
            vehicleId: vehicle.vehicle_id,
            vehicle: vehicleLabel(vehicle),
            registration: vehicle.reg_number?.trim() || null,
            stage: vehicle.contract_stage,
            managementRate: liveRate > 0 ? liveRate : null,
            state: signed ? 'signed' : live.length > 0 ? 'out' : 'missing',
            signedAt: referent?.signed_at ?? null,
            allInCost: referent?.all_in_cost ?? null,
            awaitingSeal: signed && !sealed,
            // The gate reads a NULL frozen rate as agreeing with anything, by name.
            rateSuperseded: signed && frozenRate != null && round2(frozenRate) !== round2(liveRate),
            // The pipeline's own row IS the contract's vehicle — `latest_c` is joined on
            // `c.vehicle_id = v.id` — so this is the gate's comparison, read off the list already
            // in hand rather than fetched again.
            vehicleSuperseded: signed && !!referent && referent.vehicle_id !== vehicle.vehicle_id,
            blockedByRate: !signed && live.length === 0 && liveRate <= 0,
            open: OPEN_STAGES.has(vehicle.contract_stage ?? ''),
        });
    }

    return rows.sort((a, b) => b.contractId - a.contractId);
}

/** The counts the sentence above the table is built from. */
export function partnerAgreementsSummary(rows: PartnerAgreementRow[]): PartnerAgreementsSummary {
    let total = 0;
    let signed = 0;
    let closed = 0;
    for (const row of rows) {
        if (!row.open) {
            closed += 1;
            continue;
        }
        total += 1;
        if (row.state === 'signed') signed += 1;
    }
    return { total, signed, outstanding: total - signed, closed };
}

/** The progress sentence. Numbers agree with their nouns; an empty partner says so plainly. */
export function partnerAgreementsCountLine(summary: PartnerAgreementsSummary): string {
    if (summary.total === 0) return 'No open contracts on this partner’s vehicles.';
    const noun = summary.total === 1 ? 'vehicle' : 'vehicles';
    const verb = summary.total === 1 ? 'has' : 'have';
    return `${summary.signed} of ${summary.total} ${noun} under contract ${verb} a signed Vehicle Schedule.`;
}

/** What the sentence leaves out, said out loud, or null when it leaves nothing out. */
export function partnerAgreementsClosedNote(summary: PartnerAgreementsSummary): string | null {
    if (summary.closed === 0) return null;
    const noun = summary.closed === 1 ? 'contract has' : 'contracts have';
    return `${summary.closed} closed ${noun} ended and ${summary.closed === 1 ? 'is' : 'are'} not counted.`;
}
